'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createBrowserClient } from '@/utils/supabase/client';
import { FiX, FiUser, FiLogIn } from 'react-icons/fi';

export default function AuthStatusIndicator() {
  const [isVisible, setIsVisible] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const router = useRouter();
  const supabase = createBrowserClient();

  useEffect(() => {
    // Check if user has previously dismissed the indicator
    const dismissed = localStorage.getItem('authIndicatorDismissed');
    if (dismissed === 'true') {
      setIsVisible(false);
    }

    // Check authentication status
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setIsAuthenticated(true);
          setUsername(user.email?.split('@')[0] || 'User');
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setIsAuthenticated(true);
        setUsername(session.user.email?.split('@')[0] || 'User');
      } else {
        setIsAuthenticated(false);
        setUsername(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('authIndicatorDismissed', 'true');
  };

  const handleAction = () => {
    if (isAuthenticated) {
      router.push('/dashboard');
    } else {
      router.push('/signin');
    }
  };

  if (!isVisible || isLoading) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
      <div className="bg-base-100 border border-base-300 rounded-lg shadow-lg p-4 flex items-center gap-3 max-w-xs">
        <div className="flex-shrink-0">
          {isAuthenticated ? (
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-content">
              <span className="font-semibold text-sm">
                {username?.charAt(0).toUpperCase()}
              </span>
            </div>
          ) : (
            <div className="w-10 h-10 bg-base-300 rounded-full flex items-center justify-center">
              <FiUser className="w-5 h-5 text-base-content" />
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <p className="text-sm font-medium text-base-content">
            {isAuthenticated ? `Welcome back, ${username}!` : 'Not signed in'}
          </p>
          <button
            onClick={handleAction}
            className="text-xs text-primary hover:text-primary-focus flex items-center gap-1 mt-1 transition-colors"
          >
            {isAuthenticated ? (
              <>Go to Dashboard</>
            ) : (
              <>
                <FiLogIn className="w-3 h-3" />
                Sign In
              </>
            )}
          </button>
        </div>
        
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-base-content/50 hover:text-base-content transition-colors"
          aria-label="Dismiss auth indicator"
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}